import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Loader } from '@/components/common/Loader'
import { toast } from '@/components/common/Toast'
import {
  useCreatePicklistEntry,
  useDeletePicklistEntry,
  usePicklistByKind,
  type PicklistEntry,
  type PicklistKind,
} from '@/modules/picklist/services/picklist'
import { useTranslations } from '@/hooks/useTranslations'
import { formatDate, DateFormat } from '@/utils/date'

type Props = {
  kind: PicklistKind
}

export function PicklistManagementList({ kind }: Props) {
  const t = useTranslations('admin.picklist_management')
  const { data, isLoading, refetch } = usePicklistByKind(kind)
  const createMut = useCreatePicklistEntry()
  const deleteMut = useDeletePicklistEntry()
  const [modalOpen, setModalOpen] = useState(false)
  const [label, setLabel] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<PicklistEntry | null>(null)

  const rows = data?.data ?? []
  const modalTitle =
    kind === 'durian_type'
      ? t('modal_title_durian')
      : kind === 'grade'
        ? t('modal_title_grade')
        : kind === 'strap_band'
          ? t('modal_title_strap')
          : t('modal_title_expense')
  const placeholder =
    kind === 'durian_type'
      ? t('placeholder_durian')
      : kind === 'grade'
        ? t('placeholder_grade')
        : kind === 'strap_band'
          ? t('placeholder_strap')
          : t('placeholder_expense')

  useEffect(() => {
    setLabel('')
  }, [modalOpen, kind])

  const handleCreate = async () => {
    const trimmed = label.trim()
    if (!trimmed) {
      toast.toastFailed(t('validation_empty'))
      return
    }
    try {
      await createMut.mutateAsync({ kind, label: trimmed })
      toast.toastSuccess(t('toast_created'))
      setModalOpen(false)
      await refetch()
    } catch (e) {
      toast.toastFailed(e instanceof Error ? e.message : t('toast_create_failed'))
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync({ id: deleteTarget.id })
      toast.toastSuccess(t('toast_deleted'))
      setDeleteTarget(null)
      await refetch()
    } catch (e) {
      toast.toastFailed(e instanceof Error ? e.message : t('toast_delete_failed'))
    }
  }

  return (
    <div className="relative mt-4 p-4 md:p-8">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
        <Button type="button" onClick={() => setModalOpen(true)}>
          {t('btn_create')}
        </Button>
      </div>

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">{t('table.no')}</TableHead>
              <TableHead>{t('table.label')}</TableHead>
              <TableHead className="hidden w-44 md:table-cell">{t('table.created_at')}</TableHead>
              <TableHead className="w-28 text-right">{t('table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  …
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  {t('empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, i) => (
                <TableRow key={row.id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">
                    {formatDate(row.created_at, DateFormat.DATE_TIME) ?? '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button type="button" variant="destructive" size="sm" onClick={() => setDeleteTarget(row)}>
                      {t('btn_delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {(createMut.isLoading || deleteMut.isLoading) && <Loader.FullScreen />}

      <AlertDialog open={modalOpen} onOpenChange={setModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{modalTitle}</AlertDialogTitle>
            <AlertDialogDescription>{t('modal_description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="picklist-label">{t('table.label')}</Label>
            <Input
              id="picklist-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={placeholder}
              maxLength={255}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleCreate()
                }
              }}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('btn_cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!label.trim() || createMut.isLoading}
              onClick={(ev) => {
                ev.preventDefault()
                void handleCreate()
              }}
            >
              {t('btn_submit')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_title')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">{t('delete_lead')}</span>
              <span className="block font-semibold text-foreground">&quot;{deleteTarget?.label}&quot;</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('btn_cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(ev) => {
                ev.preventDefault()
                void handleDelete()
              }}
            >
              {t('btn_delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
